'use client';

import { type CSSProperties, type FC, type ReactNode, useMemo } from 'react';

import clsx from 'clsx';
import { Tooltip as LibTooltip } from 'radix-ui';

import { type FloatingAlign, type FloatingSide } from '@/types/floating';
import { type WithClassName } from '@/types/react';
import { getCssSizeValue } from '@/utils/css';
import { isDefined } from '@/utils/utils';

import styles from './Tooltip.module.scss';

export type TooltipProps = WithClassName & {
  buttonClassName?: string;
  popupClassName?: string;
  popup: ReactNode;
  children: ReactNode;
  placement?: FloatingSide;
  align?: FloatingAlign;
  maxWidth?: number | string;
  wrapInsideButton?: boolean;
};

/**
 * Для правильной работы children должен быть:
 * - интерактивным элементом (click и focus)
 * - быть нативным html элементов (например button, a) или React компонентом с forwardRef
 *
 * или можно выставить wrapInsideButton, чтобы Tooltip сам выставил триггером кнопку
 */
export const Tooltip: FC<TooltipProps> = (props) => {
  const {
    className,
    buttonClassName,
    popupClassName,
    popup,
    children,
    placement = 'bottom',
    align = 'center',
    maxWidth = 300,
    wrapInsideButton,
  } = props;

  const tooltipStyle = useMemo(
    (): CSSProperties => ({
      '--tooltip-max-width': getCssSizeValue(maxWidth),
    }),
    [maxWidth]
  );

  return (
    <LibTooltip.Provider delayDuration={200}>
      <LibTooltip.Root>
        <LibTooltip.Trigger className={className} asChild>
          {wrapInsideButton ? (
            <button className={clsx(styles.wrapperButton, buttonClassName)} type="button">
              {children}
            </button>
          ) : (
            children
          )}
        </LibTooltip.Trigger>

        {isDefined(popup) && (
          <LibTooltip.Portal>
            <LibTooltip.Content
              className={clsx(styles.tooltip, popupClassName)}
              style={tooltipStyle}
              side={placement}
              align={align}
              sideOffset={4}
              collisionPadding={4}
            >
              {popup}
            </LibTooltip.Content>
          </LibTooltip.Portal>
        )}
      </LibTooltip.Root>
    </LibTooltip.Provider>
  );
};
